import { useNavigate, useLocation, Link } from "react-router-dom";
import { Home, Users, Calendar, FileText, User, Moon, Menu, LogOut, MessageCircle, Bell, Pill, Activity, Settings, TrendingUp, PenSquare, CreditCard, HelpCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Logo } from "./Logo";

interface DesktopSidebarProps {
  variant: "caregiver" | "family";
  hasPendingInvoices?: boolean;
  theme?: string;
  onToggleTheme?: () => void;
  onLogout?: () => void;
  onHamburgerClick?: () => void;
  unreadMessages?: number;
  activeShift?: { clientId: string; clientName: string; startedAt?: string; address?: string | null } | null;
  onComposeClick?: () => void;
  userName?: string | null;
  userEmail?: string | null;
  profilePhoto?: string | null;
  hiddenPaths?: string[];
}

export default function DesktopSidebar({
  variant,
  hasPendingInvoices = false,
  theme,
  onToggleTheme,
  onLogout,
  onHamburgerClick,
  unreadMessages = 0,
  activeShift,
  onComposeClick,
  userName,
  userEmail,
  profilePhoto,
  hiddenPaths = [],
}: DesktopSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { t: tf } = useTranslation("family");

  const isActive = (path: string) => {
    if (path === "/dashboard") return location.pathname === "/dashboard";
    if (path === "/family/dashboard") return location.pathname === "/family/dashboard";
    return location.pathname.startsWith(path);
  };

  const caregiverTabs = [
    { icon: Home, label: t("dashboard"), path: "/dashboard" },
    { icon: Users, label: t("clients"), path: "/clients" },
    { icon: Bell, label: t("notifications", "Notifications"), path: "/notifications" },
    { icon: MessageCircle, label: t("messages", "Messages"), path: "/messages" },
    { icon: Calendar, label: t("schedule"), path: "/schedule" },
    { icon: FileText, label: t("invoices"), path: "/invoices" },
    { icon: TrendingUp, label: "Your business", path: "/my-business" },
    { icon: User, label: t("profile", "Profile"), path: "/profile" },
    { icon: Settings, label: t("account"), path: "/account" },
    { icon: HelpCircle, label: t("help", "Help"), path: "/help" },
  ];

  const familyTabs = [
    { icon: Home, label: tf("nav.dashboard", "Dashboard"), path: "/family/dashboard" },
    { icon: MessageCircle, label: tf("nav.messages", "Messages"), path: "/family/messages" },
    { icon: Calendar, label: tf("nav.schedule", "Schedule"), path: "/family/schedule" },
    { icon: Activity, label: tf("nav.vitals", "Vitals"), path: "/family/vitals" },
    { icon: Pill, label: tf("nav.medications", "Medications"), path: "/family/medications" },
    { icon: CreditCard, label: tf("nav.billing", "Billing"), path: "/family/billing" },
    { icon: User, label: tf("nav.caregiver", "Caregiver"), path: "/family/caregiver" },
  ];

  const tabs = (variant === "caregiver" ? caregiverTabs : familyTabs).filter(t => !hiddenPaths.includes(t.path));

  return (
    <aside className="desktop-sidebar" aria-label="Sidebar navigation">
      {/* Logo — icon + wordmark */}
      <div style={{ marginBottom: 32, paddingLeft: 4 }}>
        <button
          onClick={() => navigate("/")}
          aria-label="Go to home"
          style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 10 }}
        >
          {/* Lavender logo mark */}
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" className="sidebar-logo-icon" style={{ flexShrink: 0 }}>
            <rect width="28" height="28" rx="8" fill="var(--color-primary)" />
            <path d="M8 14C8 10.134 11.134 7 15 7C17.21 7 19.21 8.21 20.35 10.05C20.78 10.72 21 11.5 21 12.31C21 14.66 19.21 16.64 16.88 16.95C16.29 17.03 15.69 16.97 15.12 16.78C14.22 16.48 13.45 15.88 12.93 15.1C12.33 14.2 12 13.13 12 12C12 10.9 12.45 9.9 13.17 9.17" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="10" cy="18" r="2" fill="#D4A843" />
          </svg>
          <span className="sidebar-logo-full"><Logo size="default" onDark="auto" /></span>
        </button>
      </div>

      {/* Hamburger toggle */}
      {onHamburgerClick && (
        <button
          onClick={onHamburgerClick}
          aria-label="Toggle menu"
          title="Toggle menu"
          className="sidebar-nav-item"
          style={{
            display: "flex", alignItems: "center", gap: 16,
            padding: "12px 16px", marginBottom: 8,
            background: "none", border: "none", borderRadius: 10,
            cursor: "pointer", fontFamily: "inherit", fontSize: 20, fontWeight: 500,
            color: "var(--color-text-muted)", width: "100%",
          }}
        >
          <Menu size={24} aria-hidden="true" />
          <span className="nav-label">Menu</span>
        </button>
      )}

      {/* Navigation */}
      <nav aria-label="Primary navigation" style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = isActive(tab.path);
          // Badge hidden when the tab is active — you're already on the
          // page, the dot just adds noise. UX scan flagged the persistent
          // dot on Invoices as "stale unread indicator" when really it
          // means "actions needed here". Distinct colors below clarify.
          const badgeCount = active
            ? 0
            : (tab.path === "/invoices" && hasPendingInvoices) ? 1
            : (tab.path === "/messages" && unreadMessages > 0) ? unreadMessages
            : 0;
          const hasBadge = badgeCount > 0;
          // Invoices badge is "action needed" (amber), not "alarm" (red).
          // Messages badge is unread count (lavender, conventional).
          const badgeColor = tab.path === "/invoices"
            ? "var(--color-warning)"
            : "var(--color-primary)";
          return (
            <Link
              key={tab.path}
              to={tab.path}
              aria-current={active ? "page" : undefined}
              aria-label={hasBadge ? `${tab.label} (pending invoices)` : tab.label}
              title={tab.label}
              className="sidebar-nav-item"
              style={{
                display: "flex", alignItems: "center", gap: 16,
                padding: "12px 16px",
                background: active ? "var(--color-primary-light)" : "none",
                border: "none",
                borderLeft: active ? "3px solid var(--color-primary)" : "3px solid transparent",
                borderRadius: "0 10px 10px 0",
                cursor: "pointer", fontFamily: "inherit", fontSize: 20,
                fontWeight: active ? 700 : 500,
                color: active ? "var(--color-primary)" : "var(--color-sidebar-text, var(--color-text))",
                textDecoration: "none",
                position: "relative", width: "100%",
                transition: "background 0.15s",
              }}
            >
              <div style={{ position: "relative", display: "inline-flex" }}>
                <Icon size={24} aria-hidden="true" />
                {hasBadge && (
                  <span
                    aria-hidden="true"
                    style={{
                      position: "absolute", top: -4, right: -6,
                      minWidth: 16, height: 16, borderRadius: 8,
                      background: badgeColor,
                      border: "2px solid var(--color-surface)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 9, fontWeight: 700, color: "#fff",
                      padding: "0 3px", lineHeight: 1,
                    }}
                  >
                    {badgeCount > 1 ? badgeCount : ""}
                  </span>
                )}
              </div>
              <span className="nav-label">{tab.label}</span>
            </Link>
          );
        })}

        {/* Logout for family variant */}
        {variant === "family" && onLogout && (
          <button
            onClick={onLogout}
            aria-label="Log out"
            title="Log out"
            className="sidebar-nav-item"
            style={{
              display: "flex", alignItems: "center", gap: 16,
              padding: "12px 16px", marginTop: 8,
              background: "none", border: "none", borderLeft: "3px solid transparent",
              borderRadius: "0 10px 10px 0",
              cursor: "pointer", fontFamily: "inherit", fontSize: 20, fontWeight: 500,
              color: "var(--color-text-muted)", width: "100%",
            }}
          >
            <LogOut size={24} aria-hidden="true" />
            <span className="nav-label">{tf("nav.logout", "Log out")}</span>
          </button>
        )}
      </nav>

      {/* Bottom section — shift status + post + profile */}
      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
        {/* Active shift card */}
        {variant === "caregiver" && activeShift && (
          <button
            onClick={() => navigate(`/clients/${activeShift.clientId}`)}
            aria-label={`Active shift with ${activeShift.clientName} — open client`}
            title={`Active shift: ${activeShift.clientName}`}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 12px",
              background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
              border: "none", borderRadius: "var(--radius-md)",
              cursor: "pointer", fontFamily: "inherit", color: "white", width: "100%", textAlign: "left",
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#6EE7B7", boxShadow: "0 0 6px rgba(110,231,183,0.6)", animation: "pulse 2s infinite", flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.03em", opacity: 0.85 }}>CLOCKED IN</div>
              <div className="nav-label" style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{activeShift.clientName}</div>
            </div>
          </button>
        )}

        {/* Post button */}
        {variant === "caregiver" && activeShift && onComposeClick && (
          <button
            onClick={onComposeClick}
            aria-label="Compose post"
            title="Compose post"
            className="sidebar-nav-item"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              padding: "8px 14px",
              background: "var(--color-primary)", border: "none", borderRadius: 9999,
              cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700,
              color: "var(--color-on-primary)", width: "100%",
            }}
          >
            <PenSquare size={16} aria-hidden="true" />
            <span className="nav-label">Post</span>
          </button>
        )}
      </div>

      {/* User profile */}
      {userName && (
        <button
          onClick={() => navigate(variant === "caregiver" ? "/profile" : "/family/account")}
          aria-label={`Open ${variant === "caregiver" ? "profile" : "account"} for ${userName}`}
          title={userName}
          style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "10px 16px",
            background: "none", border: "none", borderRadius: 10,
            cursor: "pointer", fontFamily: "inherit", width: "100%", textAlign: "left",
          }}
        >
          <div style={{
            width: 38, height: 38, borderRadius: "50%",
            background: profilePhoto ? "transparent" : "var(--color-primary-light)",
            color: "var(--color-primary)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: 700, fontSize: 14, flexShrink: 0, overflow: "hidden",
          }}>
            {profilePhoto ? (
              <img src={profilePhoto} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              userName.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--color-sidebar-text, var(--color-text))", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{userName}</div>
            {userEmail && <div style={{ fontSize: 11, color: "var(--color-sidebar-text, var(--color-text-muted))", opacity: 0.7, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{userEmail}</div>}
          </div>
        </button>
      )}

      {/* Dark mode toggle switch */}
      {onToggleTheme && (
        <button
          onClick={onToggleTheme}
          aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "10px 16px",
            background: "none", border: "none", borderRadius: 10,
            cursor: "pointer", fontFamily: "inherit", width: "100%",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Moon size={18} color="var(--color-text-muted)" aria-hidden="true" />
            <span className="nav-label" style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-muted)" }}>Dark Mode</span>
          </div>
          {/* Toggle switch */}
          <span className="nav-label" style={{
            width: 36, height: 20, borderRadius: 10, position: "relative",
            background: theme === "dark" ? "var(--color-primary)" : "var(--color-border)",
            transition: "background 0.2s",
            display: "inline-block", flexShrink: 0,
          }}>
            <span style={{
              width: 16, height: 16, borderRadius: "50%", background: "#fff",
              position: "absolute", top: 2,
              left: theme === "dark" ? 18 : 2,
              transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
            }} />
          </span>
        </button>
      )}
    </aside>
  );
}
